import { Calendar, Settings2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SheetConfirmationCloseDialog } from '../SheetConfirmationCloseDialog';
import { ActivationBanner } from './ActivationBanner/ActivationBanner';
import { DescriptionField } from './DescriptionField/DescriptionField';
import { RunConditionsSection } from './RunConditions/RunConditionsSection';
import { WhenToRunSection } from './WhenToRunSection';
import { AiParameters } from '@/Domain/components/Action/HttpRequestActionSheet/AIParameter/AIParameter';
import { ApiData } from '@/Domain/components/Action/HttpRequestActionSheet/ApiData/ApiData';
import { ResponseFilter } from '@/Domain/components/Action/HttpRequestActionSheet/ResponseFilter/ResponseFilter';
import { UrlField } from '@/Domain/components/Action/HttpRequestActionSheet/UrlField/UrlField';
import { MemoryCapture } from '@/Domain/components/Action/MemoryCapture/MemoryCapture';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/Domain/components/Shared/Accordion/Accordion';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Field, FieldDescription, FieldLabel } from '@/Domain/components/Shared/Field/Field';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/Domain/components/Shared/Form/Form';
import { FormMessage } from '@/Domain/components/Shared/FormCustom/FormCustom';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { ScrollNav } from '@/Domain/components/Shared/ScrollNav/ScrollNav';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import { HttpMethodEnum } from '@/Domain/enum/HttpMethod.enum';
import { useActionForm } from '@/Domain/hooks/Action/ApiAction/useHttpRequestActionFormV2.hook';
import { CAL_LINK, useCalModal } from '@/Domain/hooks/useCalModal.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

export const HttpRequestActionSheet = () => {
  const { t } = useTranslation('actions_page', { keyPrefix: 'sheet' });
  const { t: tCommon } = useTranslation('common');
  const { isOpen, toggleModal, closeModal, params } = useModalStore();
  const openCalModal = useCalModal();

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [scrollContainer, setScrollContainer] = useState<HTMLElement | null>(null);

  const { httpRequestAction, actionTemplate } = params || {};
  const { form, onSubmit, onInvalid } = useActionForm(httpRequestAction, actionTemplate);
  const executionMode = form.watch('execution_mode');

  const handleClose = () => {
    if (form.formState.isDirty && Object.keys(form.formState.dirtyFields).length > 0) {
      setConfirmationModalOpen(true);
    } else {
      closeModal();
      form.reset();
    }
  };

  const handleConfirmClose = () => {
    setConfirmationModalOpen(false);
    closeModal();
    form.reset();
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose],
  );

  useEffect(() => {
    if (!isOpen(MODAL_NAMES.HTTP_REQUEST_ACTION_SHEET)) return;

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen(MODAL_NAMES.HTTP_REQUEST_ACTION_SHEET), handleKeyDown]);

  const handleScheduleCall = () => openCalModal(CAL_LINK.productDemo);

  const SECTIONS = useMemo(
    () => [
      { id: 'api_action_name', label: t('nav.api_name') },
      { id: 'execution_mode', label: t('nav.when_to_run') },
      { id: 'data_to_collect', label: t('nav.what_to_ask_first') },
      { id: 'api_endpoint', label: t('nav.api_endpoint') },
      { id: 'description_ai', label: t('nav.description_ai') },
      { id: 'memory_capture', label: t('nav.save_to_memory') },
      { id: 'need_help', label: t('nav.need_help') },
    ],
    [t],
  );

  return (
    <Form {...form}>
      <Sheet
        open={isOpen(MODAL_NAMES.HTTP_REQUEST_ACTION_SHEET)}
        onOpenChange={() => toggleModal(MODAL_NAMES.HTTP_REQUEST_ACTION_SHEET)}
      >
        <SheetContent
          aria-describedby={undefined}
          className="!max-w-[100vw] xl:!max-w-[85%] 2xl:!max-w-7xl w-full gap-0"
          tabIndex={undefined}
          asChild
        >
          <form className="h-full">
            <SheetHeader className="pb-4">
              <SheetTitle>{t('title')}</SheetTitle>
              <div className="flex items-center gap-2 pr-1">
                <Button
                  onClick={form.handleSubmit(onSubmit, onInvalid)}
                  data-testid="http-request-action-sheet-submit-button"
                >
                  {t('button.save_changes')}
                </Button>
                <IconButton variant="secondary" onClick={handleClose}>
                  <X className="size-4" />
                </IconButton>
              </div>
            </SheetHeader>

            <div className="grid grid-cols-1 2xl:grid-cols-[0.2fr_auto_1fr] h-full overflow-hidden">
              <ScrollNav sections={SECTIONS} scrollContainer={scrollContainer} />
              <Separator orientation="vertical" className="hidden 2xl:flex mr-4" />

              <section
                ref={setScrollContainer}
                className="relative px-1 h-full custom-scroll overflow-y-auto scroll-stable pt-6"
              >
                {httpRequestAction && <ActivationBanner action={httpRequestAction} />}

                <div className="space-y-7">
                  <div id="api_action_name">
                    <FormField
                      name="name"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel>{t('form.name.label')}</FormLabel>
                          <FormDescription>{t('form.name.description')}</FormDescription>
                          <FormControl>
                            <Input
                              data-testid="http-request-action-form-name-input"
                              autoFocus
                              placeholder={t('form.name.placeholder')}
                              error={!!fieldState.error}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage error={fieldState.error} />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator />

                  <WhenToRunSection form={form} />

                  <Separator />

                  <div id="data_to_collect" className="space-y-7">
                    <Field>
                      <FieldLabel>{t('form.data_to_collect.label')}</FieldLabel>
                      <FieldDescription>{t('form.data_to_collect.description')}</FieldDescription>
                    </Field>

                    <AiParameters form={form} />
                  </div>

                  <Separator />

                  <div id="api_endpoint" className="space-y-7">
                    <Field>
                      <FieldLabel>{t('form.api_endpoint.label')}</FieldLabel>
                      <FieldDescription>{t('form.api_endpoint.description')}</FieldDescription>
                    </Field>

                    <section className="grid grid-cols-[200px_1fr] gap-3 w-full">
                      <FormField
                        name="method"
                        render={({ field }) => (
                          <FormItem className="py-1">
                            <FormLabel> {t('form.api_endpoint.action_method')}</FormLabel>
                            <FormControl>
                              <Select value={field.value} onValueChange={field.onChange}>
                                <SelectTrigger className="w-full" size="md" ref={field.ref}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.entries(HttpMethodEnum).map(([key, value]) => (
                                    <SelectItem key={key} value={key}>
                                      {value}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <UrlField />
                    </section>

                    <ApiData form={form} actionId={httpRequestAction?.id} />
                  </div>

                  <Separator />

                  <div id="description_ai">
                    <DescriptionField />
                  </div>
                  <div id="thinking_message">
                    <FormField
                      name="thinking_message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('form.thinking_message.label')}</FormLabel>
                          <FormDescription>
                            {t('form.thinking_message.description')}
                          </FormDescription>
                          <FormControl>
                            <AutosizeTextarea
                              data-testid="http-request-action-form-thinking-message-input"
                              placeholder={t('form.thinking_message.placeholder')}
                              minHeight={100}
                              className="px-4 mb-1"
                              {...field}
                            />
                          </FormControl>

                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator />

                  <div id="memory_capture" className="space-y-7">
                    <Field>
                      <FieldLabel>{t('form.memory_capture.label')}</FieldLabel>
                      <FieldDescription>{t('form.memory_capture.description')}</FieldDescription>
                      <a
                        href="https://docs.quickchat.ai/ai-agent/actions/#save-to-memory"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${tCommon('learn')}: ${t('form.memory_capture.label')}`}
                        className="mt-1 inline-block text-sm text-primary hover:underline"
                      >
                        {tCommon('learn')}
                      </a>
                    </Field>

                    <MemoryCapture form={form} name="response_metadata_captures" />
                  </div>

                  <Accordion
                    type="single"
                    collapsible
                    className="w-full"
                    data-testid="http-request-action-sheet-advanced-settings-accordion"
                  >
                    <AccordionItem value="advanced_settings" className="border-b-0">
                      <AccordionTrigger className="text-foreground font-medium px-1 py-3 cursor-pointer">
                        <div className="flex items-center gap-3">
                          <Settings2 className="h-4 w-4" />
                          {t('form.advanced_settings.label')}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-2 pb-0 flex flex-col gap-7">
                        {executionMode !== 'when_metadata_matches' && (
                          <RunConditionsSection form={form} />
                        )}

                        <div id="response_filter" className="space-y-7">
                          <Field>
                            <FieldLabel>{t('form.response_filter.label')}</FieldLabel>
                            <FieldDescription>
                              <Trans
                                t={t}
                                i18nKey="form.response_filter.description"
                                components={{
                                  1: (
                                    <a
                                      href="https://goessner.net/articles/JsonPath/"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-primary hover:underline"
                                    />
                                  ),
                                }}
                              />
                            </FieldDescription>
                            <a
                              href="https://docs.quickchat.ai/ai-agent/actions/#response-filter"
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={`${tCommon('learn')}: ${t('form.response_filter.label')}`}
                              className="mt-1 inline-block text-sm text-primary hover:underline"
                            >
                              {tCommon('learn')}
                            </a>
                          </Field>

                          <ResponseFilter form={form} />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  <Separator />

                  <div id="need_help">
                    <div className="flex py-[12vh] xl:py-[26vh] space-y-2 flex-col items-center">
                      <img
                        src="/static/assets/qc-api.webp"
                        alt="AI Insights graphic"
                        className="w-64 h-64 object-cover"
                      />
                      <p className="font-medium">{t('form.need_help.title')}</p>
                      <p className="max-w-xl text-center text-sm text-muted-foreground">
                        <Trans
                          t={t}
                          i18nKey={'form.need_help.description'}
                          components={{
                            docs_link: (
                              <a
                                href="https://docs.quickchat.ai/"
                                className="font-medium text-foreground"
                                target="_blank"
                                rel="noreferrer"
                              />
                            ),
                          }}
                        />
                      </p>
                      <Button variant="secondary" onClick={handleScheduleCall} className="mt-4">
                        <Calendar className="size-4" />
                        {t('button.schedule_a_call')}
                      </Button>
                    </div>
                  </div>
                </div>
              </section>
            </div>
            <SheetConfirmationCloseDialog
              confirmationModalOpen={confirmationModalOpen}
              setConfirmationModalOpen={setConfirmationModalOpen}
              handleConfirmClose={handleConfirmClose}
            />
          </form>
        </SheetContent>
      </Sheet>
    </Form>
  );
};
